/**
 * useBlockedShipmentsMain - Hook principal para gestionar la vista de shipments bloqueados
 *
 * Orquesta todos los aspectos de la gestión de shipments bloqueados:
 * - Carga de datos paginados con infinite scroll
 * - Gestión de filtros avanzados y búsqueda (sincronizados con URL)
 * - Sorting de columnas (id, ref, order_created_at, processed_at, date_tracking_update, last_status_at)
 * - Carga dinámica de valores para filtros (shops, payment methods, couriers, teams, countries)
 * - Sincronización bidireccional con query params de la URL
 *
 * @returns {Object} Estado completo de la vista con datos, handlers, filtros y configuración
 */

import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import { useBlockedShipments } from './useBlockedShipments';
import { getInvoicesOrdersFilterOptions, LoadColumnValuesCallback } from '../config/filterOptions';
import { FiltersApiAdapter } from '../adapters/FiltersApiAdapter';
import { TeamsApiAdapter } from '../adapters/TeamsApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useCountries } from './useCountries';

const fieldToApiMapping: Record<string, string> = {
  id: 'id',
  order_id: 'order_id',
  ref: 'ref',
  order_created_at: 'order_created_at',
  processed_at: 'processed_at',
  date_tracking_update: 'date_tracking_update',
  last_status_at: 'last_status_at',
};

const serializeConditions = (conditions: FilterConditionOutput[]) =>
  JSON.stringify(
    conditions.map(({ column, action, value }) => ({
      column,
      action,
      value,
    })),
  );

const parseFiltersFromParams = (value: unknown): FilterConditionOutput[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value as FilterConditionOutput[];
};

export const useBlockedShipmentsMain = () => {
  const { t } = useTranslation('invoicesOrdersModule');

  // 🎯 Cargar países para el filtro
  const { countries } = useCountries();

  // 🎯 Función para cargar opciones dinámicamente
  const loadColumnValues: LoadColumnValuesCallback = useCallback(
    async (columnValue: string) => {
      try {
        const httpClient = createHttpClient(apiClientWH);
        const filtersAdapter = new FiltersApiAdapter(httpClient);

        switch (columnValue) {
          case 'shop_id': {
            const shops = await filtersAdapter.getShops();
            return shops.map((shop) => ({
              label: shop.name,
              value: String(shop.id),
            }));
          }
          case 'payment_method_id': {
            const paymentMethods = await filtersAdapter.getPaymentMethods();
            return paymentMethods.map((method) => ({
              label: method.name,
              value: String(method.id),
            }));
          }
          case 'courier_id': {
            const couriers = await filtersAdapter.getCouriers();
            return couriers.map((courier) => ({
              label: courier.name,
              value: String(courier.id),
            }));
          }
          case 'team_id': {
            const teamsAdapter = new TeamsApiAdapter(httpClient);
            const teams = await teamsAdapter.getTeamsChild();
            return teams.map((team) => ({
              label: team.name,
              value: team.id.toString(),
            }));
          }
          case 'status_ids': {
            const { orderStatusFilterOptions } = await import('../config/orderStatusConfig');
            return orderStatusFilterOptions.map((opt) => ({
              label: t(opt.labelKey),
              value: String(opt.status_id),
            }));
          }
          case 'shipment_status_ids': {
            const shipmentStatuses = await filtersAdapter.getShipmentStatuses();
            return shipmentStatuses.map((s) => ({
              label: s.name,
              value: String(s.id),
            }));
          }
          default:
            return [];
        }
      } catch {
        return [];
      }
    },
    [t],
  );

  const {
    params: queryParams,
    updateParam,
    updateParams,
  } = useQueryParams({
    search: { type: 'string', default: '' as string },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: '' as string },
    sortDirection: { type: 'string', default: '' as string },
  });

  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(() =>
    parseFiltersFromParams(queryParams.filters),
  );
  const filterConditionsSerializedRef = useRef<string>(serializeConditions(filterConditions));
  const [searchTerm, setSearchTerm] = useState<string>((queryParams.search ?? '').trim());
  const [sorting, setSorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(() => {
    const sortField = queryParams.sortBy;
    if (!sortField) {
      return null;
    }

    const direction = queryParams.sortDirection === 'desc' ? 'desc' : 'asc';
    return {
      field: sortField,
      direction,
    };
  });

  // Sincronización con query params
  useEffect(() => {
    const paramSearch = (queryParams.search ?? '').trim();
    setSearchTerm((prev) => (prev === paramSearch ? prev : paramSearch));
  }, [queryParams.search]);

  useEffect(() => {
    const nextConditions = parseFiltersFromParams(queryParams.filters);
    const nextSerialized = serializeConditions(nextConditions);

    if (nextSerialized !== filterConditionsSerializedRef.current) {
      filterConditionsSerializedRef.current = nextSerialized;
      setFilterConditions(nextConditions);
    }
  }, [queryParams.filters]);

  useEffect(() => {
    const sortField = queryParams.sortBy;
    if (!sortField) {
      setSorting((prev) => (prev === null ? prev : null));
      return;
    }

    const direction = queryParams.sortDirection === 'desc' ? 'desc' : 'asc';

    setSorting((prev) => {
      if (prev && prev.field === sortField && prev.direction === direction) {
        return prev;
      }

      return {
        field: sortField,
        direction,
      };
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  // Estados de columnas visibles
  const tableColumns = [
    { accessorKey: 'order', header: t('table.order') },
    { accessorKey: 'customer', header: t('table.customer') },
    { accessorKey: 'shop', header: t('table.shop') },
    { accessorKey: 'shipping_date', header: t('table.shipment_date') },
    { accessorKey: 'internal_status', header: t('table.status') },
    { accessorKey: 'logistic_status', header: t('table.logistic_status') },
    { accessorKey: 'courier', header: t('table.courier') },
    { accessorKey: 'reference', header: t('table.reference') },
    { accessorKey: 'order_price', header: t('table.total') },
    { accessorKey: 'block_reason', header: t('table.block_reason') },
    { accessorKey: 'block_reason_description', header: t('table.block_reason_description') },
    { accessorKey: 'block_comment', header: t('table.block_comment') },
    { accessorKey: 'blocked_by', header: t('table.blocked_by') },
  ];

  const [selectedColumns, setSelectedColumns] = useState<string[]>(tableColumns.map((col) => col.accessorKey));

  // Columnas que son parámetros de URL (no advanced_filters)
  const URL_PARAM_COLUMNS = new Set([
    'courier_id',
    'team_id',
    'shop_id',
    'payment_method_id',
    'period',
    'status_ids',
    'shipment_status_ids',
  ]);

  const columnToApiParam: Record<string, string> = {
    courier_id: 'courier_ids',
    team_id: 'team_ids',
    shop_id: 'shop_ids',
    payment_method_id: 'payment_method_ids',
    period: 'period',
    status_ids: 'status_ids',
    shipment_status_ids: 'shipment_status_ids',
  };

  const toNumberArray = (val: unknown): number[] => {
    if (val == null) return [];
    const arr = Array.isArray(val) ? val : [val];
    return arr.map((v) => (typeof v === 'number' ? v : parseInt(String(v), 10))).filter((n) => !Number.isNaN(n));
  };

  // Procesar filtros
  const processedFilters = useMemo(() => {
    try {
      // Excluir columnas que son parámetros de URL (van en query string, no en advanced_filters)
      // financial_status no está permitido en el endpoint /invoices/shipments/block
      const conditionsForAdvancedFilter = filterConditions.filter(
        (c) => !URL_PARAM_COLUMNS.has(c.column) && c.column !== 'financial_status',
      );

      const searchFilterFromConditions =
        conditionsForAdvancedFilter.length > 0
          ? {
              filters: conditionsForAdvancedFilter.map((condition) => {
                const requiresArray = ['in', 'not_in'].includes(condition.action);
                const isNullOperator = ['is_null', 'is_not_null'].includes(condition.action);

                const value = isNullOperator
                  ? undefined
                  : requiresArray
                    ? Array.isArray(condition.value)
                      ? condition.value
                      : [condition.value]
                    : condition.value;

                return {
                  column: condition.column,
                  operator: condition.action,
                  ...(value !== undefined && { value: value }),
                };
              }),
              logic: 'AND',
            }
          : undefined;

      // Extraer filtros directos (courier_ids, team_ids, etc.) desde filterConditions
      const directFilters: Record<string, number[] | string> = {};
      for (const c of filterConditions) {
        const paramKey = columnToApiParam[c.column];
        if (!paramKey || ['is_null', 'is_not_null'].includes(c.action)) continue;
        if (paramKey === 'period') {
          const v = c.value;
          directFilters.period = Array.isArray(v) ? ((v[0] as string) ?? '') : String(v ?? '');
        } else {
          const nums = toNumberArray(c.value);
          if (nums.length > 0) {
            directFilters[paramKey] = nums;
          }
        }
      }

      // Mapear el campo de sorting a los parámetros de la API
      const apiField = sorting?.field ? fieldToApiMapping[sorting.field] || 'id' : 'id';
      const apiDirection = sorting?.direction || 'desc';

      return {
        sortBy: apiField,
        sortDirection: apiDirection,
        ...(searchFilterFromConditions && {
          searchFilter: searchFilterFromConditions,
        }),
        ...directFilters,
      };
    } catch {
      return {};
    }
  }, [filterConditions, sorting]);

  // Memoizar el objeto de filtros para evitar re-renders infinitos
  const queryFilters = useMemo(
    () => ({
      search: searchTerm,
      ...processedFilters,
      sorting,
    }),
    [searchTerm, processedFilters, sorting],
  );

  // Hooks de datos
  const {
    blockedShipments,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    total,
  } = useBlockedShipments(queryFilters);

  // Detectar si estamos ordenando (isLoading + sorting activo)
  const isSortingActive = useMemo(() => {
    return isFetching && sorting !== null;
  }, [isFetching, sorting]);

  // Handlers de filtros
  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      const serialized = serializeConditions(conditions);

      if (filterConditionsSerializedRef.current === serialized) {
        return;
      }

      filterConditionsSerializedRef.current = serialized;
      setFilterConditions(conditions);
      updateParam('filters', conditions);
    },
    [updateParam],
  );

  const handleSearch = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      setSearchTerm((prev) => (prev === trimmedValue ? prev : trimmedValue));
      updateParam('search', trimmedValue);
    },
    [updateParam],
  );

  const handleColumnsChange = useCallback((newSelected: string[]) => {
    setSelectedColumns(newSelected);
  }, []);

  const handleSortingChange = useCallback(
    (newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
      setSorting(newSorting);

      if (newSorting && newSorting.field) {
        updateParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      } else {
        updateParams({
          sortBy: '',
          sortDirection: '',
        });
      }
    },
    [updateParams],
  );

  // Props para componentes
  const blockedShipmentsViewProps = useMemo(
    () => ({
      blockedShipments,
      isLoading,
      isFetching,
      isSortingActive,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      selectedColumns: [...selectedColumns, 'actions'],
      refetch,
      sorting,
      onSortingChange: handleSortingChange,
    }),
    [
      blockedShipments,
      isLoading,
      isFetching,
      isSortingActive,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      selectedColumns,
      refetch,
      sorting,
      handleSortingChange,
    ],
  );

  // Detectar si hay filtros avanzados activos
  const hasActiveFilters = useMemo(() => {
    return filterConditions.length > 0;
  }, [filterConditions.length]);

  return {
    // Estados
    filterConditions,
    selectedColumns,
    tableColumns,
    sorting,
    searchTerm,

    // Datos
    blockedShipments,
    isLoading,
    isFetching,
    isSortingActive,
    error,
    total,

    // Props para componentes
    blockedShipmentsViewProps,

    // Handlers
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleSortingChange,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Estado de filtros
    hasActiveFilters,
    search: searchTerm,
    filterOptions: getInvoicesOrdersFilterOptions(t, loadColumnValues, false, countries, true),
  };
};
